import { Box, Divider } from '@mui/material';
import { Barcode } from 'scandit-web-datacapture-barcode';
import { Business_Profile_Enum } from '@kheops/graphql';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import FullTextSearch from '../common/components/FullTextSearch';
import { FeatureFlagKey, isFeatureEnabled } from '../common/utils/featureFlags';
import BarcodeCaptureButton from '../search/product/BarcodeCaptureButton';
import { currentContextAtom } from '../state';
import useUpdateBasketByBarCode from '../search/product/useUpdateBasketByBarCode';
import CompanyClassificationTagFilters from './CompanyClassificationTagFilters';
import CompanyProductSubFamiliesFilter from './CompanyProductSubFamiliesFilter';
import { refreshCompanyProductListAtom } from './state';

export interface CompanyProductFiltersProps {
  contextCompanyId?: string;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

export default function CompanyProductFilters({ contextCompanyId, searchInputRef }: CompanyProductFiltersProps): React.JSX.Element {
  const { t } = useTranslation(['discovery']);
  const { realm, companyId: userCompanyId } = useAtomValue(currentContextAtom);
  const { query } = useSearchBox();
  const { refresh } = useInstantSearch();
  const [fullTextSearchValue, setFullTextSearchValue] = useState(query || '');
  const { upsertPackagingByBarCode } = useUpdateBasketByBarCode();
  const [refreshCompanyProducts, setRefreshCompanyProducts] = useAtom(refreshCompanyProductListAtom);
  const companyProductFiltersRef = useRef<HTMLElement>(null);
  const location = useLocation();

  const displayBarCodeScanner = realm === Business_Profile_Enum.Supplier && contextCompanyId !== userCompanyId;
  const classificationEnabled = isFeatureEnabled(FeatureFlagKey.PRODUCT_CLASSIFICATION);

  const handleBarcodeCapture = (barcodes: Barcode[]): void => {
    if (barcodes[0].data) {
      setFullTextSearchValue(barcodes[0].data);
      upsertPackagingByBarCode(barcodes[0].data);
    }
  };

  useEffect(() => {
    if (refreshCompanyProducts) {
      refresh();

      setRefreshCompanyProducts(false);
    }
  }, [refreshCompanyProducts]);

  useEffect((): void => {
    if (location.state?.productFamily) {
      companyProductFiltersRef.current!.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
  });

  return (
    <>
      <Box
        ref={companyProductFiltersRef}
        sx={{
          position: 'sticky',
          top: {
            xs: 64,
            lg: 80,
          },
          zIndex: 10,
          backgroundColor: 'surface.main',
          py: 1.5,
          display: 'flex',
          alignItems: classificationEnabled ? { xs: 'stretch', lg: 'center' } : 'center',
          width: '100%',
          flexDirection: classificationEnabled ? { xs: 'column', lg: 'row' } : 'row',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: classificationEnabled ? { xs: '100%', lg: 'auto' } : undefined,
            flex: {
              xs: 1,
              lg: 'unset',
            },
          }}
        >
          <FullTextSearch
            value={fullTextSearchValue}
            onChange={(value) => setFullTextSearchValue(value)}
            inputRef={searchInputRef}
            inputProps={{
              sx: {
                height: 48,
                width: classificationEnabled ? { xs: '100%', lg: 420 } : { xs: '100%', md: 420 },
                flexShrink: 0,
              },
              placeholder: t('discovery:search_a_product'),
            }}
          />
        </Box>
        <Divider
          flexItem
          orientation="vertical"
          sx={{
            my: 1.5,
            display: {
              xs: 'none',
              lg: 'block',
            },
          }}
        />
        <CompanyProductSubFamiliesFilter />
      </Box>
      {classificationEnabled && <CompanyClassificationTagFilters />}
      {displayBarCodeScanner && <BarcodeCaptureButton sx={{ bottom: 24 }} onCapture={handleBarcodeCapture} />}
    </>
  );
}
